// === ENVOI BUG REPORT AVEC PIÈCES JOINTES ===
// Variante de BugReportManager.submit() qui envoie en multipart/form-data
// (Discord attend `payload_json` + `files[N]`) avec screenshot + save + logs
// joints. Vit dans un fichier séparé pour ne pas alourdir le manager testé.

import type { BugReport, SubmitResult } from './BugReportManager';
import { formatBugReportAsMarkdown } from './BugReportExport';

const REPORTS_KEY = 'deadtide_bug_reports';
const MAX_REPORTS = 50;
const LAST_SENT_KEY = 'deadtide_bug_last_sent';
const ANTI_SPAM_MS = 30_000;

export interface BugReportAttachments {
  screenshot?: Blob | null;
  save?: Blob | null;
  logs?: Blob | null;
}

/** Génère un ID de rapport stable. Utilisé par l'overlay pour pré-afficher l'ID. */
export function generateBugReportId(): string {
  return `bug_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

/**
 * Soumet un rapport avec pièces jointes. Comportement identique à
 * BugReportManager.submit() (anti-spam + stockage local + envoi best-effort)
 * mais utilise multipart/form-data pour porter les fichiers.
 *
 * @param presetId  Si fourni, utilisé tel quel comme `report.id`. Permet à
 *                  l'overlay d'afficher l'ID au testeur AVANT l'envoi pour
 *                  qu'il s'y retrouve dans les rapports Discord.
 */
export async function submitBugReportWithAttachments(
  data: Omit<BugReport, 'id' | 'timestamp' | 'sent' | 'sendError'>,
  attachments: BugReportAttachments,
  presetId?: string,
): Promise<SubmitResult> {
  // Anti-spam : refuser si dernier envoi < 30s
  try {
    const last = parseInt(localStorage.getItem(LAST_SENT_KEY) ?? '0', 10);
    if (Date.now() - last < ANTI_SPAM_MS) {
      const remain = Math.ceil((ANTI_SPAM_MS - (Date.now() - last)) / 1000);
      return { ok: false, sentToDiscord: false, message: `⏳ Patiente ${remain}s avant le prochain rapport.` };
    }
  } catch { /* localStorage indispo */ }

  const report: BugReport = {
    ...data,
    id: presetId ?? generateBugReportId(),
    timestamp: new Date().toISOString(),
    sent: false,
  };

  // 1. Stockage local TOUJOURS en premier
  try {
    const raw = localStorage.getItem(REPORTS_KEY);
    const reports: BugReport[] = raw ? JSON.parse(raw) : [];
    reports.unshift(report);
    if (reports.length > MAX_REPORTS) reports.splice(MAX_REPORTS);
    localStorage.setItem(REPORTS_KEY, JSON.stringify(reports));
  } catch { /* localStorage indispo */ }

  // 2. Envoi Discord
  const url = (import.meta.env.VITE_DISCORD_WEBHOOK_URL as string | undefined);
  if (!url) {
    return { ok: true, sentToDiscord: false, message: '✅ Rapport enregistré localement (webhook non configuré).' };
  }

  try {
    const hasScreenshot = !!attachments.screenshot;
    const payload = buildPayload(report, hasScreenshot);

    const form = new FormData();
    form.append('payload_json', JSON.stringify(payload));
    let i = 0;
    if (attachments.screenshot) form.append(`files[${i++}]`, attachments.screenshot, 'screenshot.png');
    if (attachments.save) form.append(`files[${i++}]`, attachments.save, 'save.json');
    if (attachments.logs) form.append(`files[${i++}]`, attachments.logs, 'logs.txt');

    // v0.39.3 — toujours joindre une version Markdown auto-suffisante du
    // rapport, pour qu'on puisse la télécharger depuis Discord et la coller
    // directement dans une session Claude sans avoir à recopier les champs
    // un par un. Format identique à celui exporté en bulk via DevScene
    // (formatBugReportAsMarkdown), donc consistent côté assistant.
    try {
      const mdContent = formatBugReportAsMarkdown(report);
      const mdBlob = new Blob([mdContent], { type: 'text/markdown' });
      form.append(`files[${i++}]`, mdBlob, 'bug-report.md');
    } catch {
      // Si le formateur plante, on continue sans le MD plutôt que de
      // bloquer l'envoi Discord — le rapport reste utilisable via le
      // embed + screenshot/save/logs.
    }

    const resp = await fetch(url, { method: 'POST', body: form });

    if (!resp.ok) {
      const errText = `Discord ${resp.status}: ${await resp.text().catch(() => '')}`.slice(0, 200);
      markStatus(report.id, false, errText);
      return { ok: true, sentToDiscord: false, message: `✅ Enregistré localement. ⚠️ ${errText}` };
    }

    markStatus(report.id, true);
    try { localStorage.setItem(LAST_SENT_KEY, String(Date.now())); } catch { /* */ }
    return { ok: true, sentToDiscord: true, message: '✅ Rapport envoyé ! Merci pour ton aide 🏴‍☠️' };
  } catch (e) {
    const errText = (e instanceof Error ? e.message : String(e)).slice(0, 200);
    markStatus(report.id, false, errText);
    return { ok: true, sentToDiscord: false, message: `✅ Enregistré localement. ⚠️ Réseau : ${errText}` };
  }
}

function buildPayload(r: BugReport, withScreenshot: boolean): object {
  const fields = [
    { name: '📝 Description', value: r.description.slice(0, 1024), inline: false },
    { name: '🎮 Version', value: `v${r.version}`, inline: true },
    { name: '🎬 Scène', value: r.scene, inline: true },
    { name: '🖥️ Résolution', value: r.resolution, inline: true },
  ];
  if (r.context) fields.push({ name: '🗺️ Contexte run', value: r.context.slice(0, 1024), inline: false });
  fields.push({ name: '🌐 User Agent', value: r.userAgent.slice(0, 200), inline: false });

  // Suffix court (5 derniers chars de l'ID) en titre pour repérage rapide :
  // Discord tronque visuellement les longs IDs en footer, là on les voit direct.
  const shortId = r.id.split('_').pop() ?? r.id;
  const embed: Record<string, unknown> = {
    title: `🐛 Bug #${shortId} — ${r.pseudo}`,
    color: 0xe74c3c,
    fields,
    timestamp: r.timestamp,
    footer: { text: r.id },
  };
  if (withScreenshot) embed.image = { url: 'attachment://screenshot.png' };

  return { username: 'Deadtide Bug Report', embeds: [embed] };
}

function markStatus(id: string, sent: boolean, error?: string): void {
  try {
    const raw = localStorage.getItem(REPORTS_KEY);
    if (!raw) return;
    const reports: BugReport[] = JSON.parse(raw);
    const r = reports.find((x) => x.id === id);
    if (r) {
      r.sent = sent;
      r.sendError = error;
      localStorage.setItem(REPORTS_KEY, JSON.stringify(reports));
    }
  } catch { /* */ }
}
